import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { buildMailManagerContext } from "./assistantPageContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast, { Toaster } from "react-hot-toast";
import {
  createEmailAudience,
  createEmailTemplate,
  deleteEmailTemplate,
  fetchDispatchLog,
  fetchEmailTemplateById,
  fetchEmailTemplateConfig,
  fetchEmailTemplateDraft,
  fetchEmailTemplates,
  fetchReminderRules,
  generateEmailTemplateDrafts,
  getAdminUser,
  hasPermission,
  previewEmailTemplate,
  saveEmailTemplateRouting,
  saveReminderSteps,
  sendTestEmail,
  updateEmailAudience,
  updateEmailTemplate,
} from "../api/adminClient";
import { AlertTriangle, Eye, Info, Mail, Trash2 } from "lucide-react";
import AudienceManagerModal from "./AudienceManagerModal";
import EmailTemplateRoutingPanel from "./EmailTemplateRoutingPanel";
import NewEmailTemplateModal from "./NewEmailTemplateModal";
import { roleGroupOptions } from "./emailRoleUtils";
import ReminderStepList from "./ReminderStepList";
import TemplatePreviewPanel from "./TemplatePreviewPanel";
import { FALLBACK_ANCHORS, FALLBACK_TOKENS } from "./emailTemplateTokens";
import { shortAudienceLabel, templateDisplay } from "./templateLabel";

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

let keySeq = 0;
function makeKey() {
  keySeq += 1;
  return `step-${Date.now()}-${keySeq}`;
}

function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

const EMPTY_ROUTING = {
  invite: { per_role: false, shared_template_id: null, designation_overrides: {} },
  resend: { per_role: false, shared_template_id: null, designation_overrides: {} },
  auto_reminder: { per_role: false, shared_template_id: null, designation_overrides: {} },
};

function MetaBadge({ children, className = "", title }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full ${className}`}
    >
      {children}
    </span>
  );
}

export default function EmailTemplatesSchedulerPage() {
  const { setAdminAssistantContext } = useOutletContext() || {};
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [steps, setSteps] = useState([]);
  const [savingSteps, setSavingSteps] = useState(false);

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [editorTemplate, setEditorTemplate] = useState(null);
  const [draft, setDraft] = useState({ label: "", subject: "", content_html: "" });
  const [htmlMode, setHtmlMode] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  // "off" = an editing tab is active; "desktop"/"mobile" = Device Preview tab.
  const [previewWidth, setPreviewWidth] = useState("off");

  const [view, setView] = useState("editor");
  const [logItems, setLogItems] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage] = useState(1);
  const [logLoading, setLogLoading] = useState(false);

  const [tokens, setTokens] = useState(FALLBACK_TOKENS);
  const [anchors, setAnchors] = useState(FALLBACK_ANCHORS);
  const [scheduler, setScheduler] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [templateRouting, setTemplateRouting] = useState(EMPTY_ROUTING);
  const [allowPerRoleEmailTemplates, setAllowPerRoleEmailTemplates] = useState(true);
  const [isClassicEmailWorkspace, setIsClassicEmailWorkspace] = useState(false);
  const [savingRouting, setSavingRouting] = useState(false);
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [savingAudiences, setSavingAudiences] = useState(false);
  const [viewing, setViewing] = useState(null); // { templateId, audienceLabel }
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogTarget, setDeleteDialogTarget] = useState(null); // template | null
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [testSendEmail, setTestSendEmail] = useState(() => getAdminUser()?.email || "");
  const [testSending, setTestSending] = useState(false);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [newTemplatePrefill, setNewTemplatePrefill] = useState(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  const openNewTemplateModal = useCallback((prefill = null) => {
    setNewTemplatePrefill(prefill);
    setNewTemplateOpen(true);
  }, []);

  const closeNewTemplateModal = useCallback(() => {
    setNewTemplateOpen(false);
    setNewTemplatePrefill(null);
  }, []);
  const [generatingAllDrafts, setGeneratingAllDrafts] = useState(false);
  const [matchingVoice, setMatchingVoice] = useState(false);

  const canManageSteps = hasPermission("invites:manage");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, rulesRes, configRes] = await Promise.all([
        fetchEmailTemplates(""),
        fetchReminderRules(),
        fetchEmailTemplateConfig(),
      ]);
      setTemplates(tplRes.data?.templates || []);
      const rawSteps = rulesRes.data?.global_default?.steps || [];
      setSteps(rawSteps.map((s) => ({ ...s, _key: s.step_id || makeKey() })));

      const cfg = configRes.data || {};
      if (cfg.tokens?.length) setTokens(cfg.tokens);
      setAnchors(cfg.anchors?.length ? cfg.anchors : FALLBACK_ANCHORS);
      setScheduler(cfg.scheduler || null);
      setAudiences(cfg.audiences || []);
      setDesignations(cfg.designations || []);
      setTemplateRouting(cfg.template_routing || EMPTY_ROUTING);
      setAllowPerRoleEmailTemplates(cfg.allow_per_role_email_templates !== false);
      setIsClassicEmailWorkspace(Boolean(cfg.is_classic_email_workspace));
    } catch (err) {
      toast.error(err.message || "Could not load templates & schedule.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!setAdminAssistantContext || loading) return;
    setAdminAssistantContext(
      buildMailManagerContext({
        templates,
        selectedTemplateId,
        editorTemplate,
        view,
        audiences,
        allowPerRoleEmailTemplates,
        isClassicEmailWorkspace,
      })
    );
  }, [
    setAdminAssistantContext,
    loading,
    templates,
    selectedTemplateId,
    editorTemplate,
    view,
    audiences,
    allowPerRoleEmailTemplates,
    isClassicEmailWorkspace,
  ]);

  useEffect(() => {
    return () => setAdminAssistantContext?.(null);
  }, [setAdminAssistantContext]);

  const refreshPreview = useCallback(async (subject, contentHtml) => {
    setPreviewLoading(true);
    try {
      const res = await previewEmailTemplate({ subject, content_html: contentHtml });
      setPreviewHtml(res.data?.html || "");
    } catch (err) {
      toast.error(err.message || "Could not render preview.");
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const selectTemplate = useCallback(
    async (id) => {
      if (!id) return;
      // The main column shows either the preview or the editor, never both.
      setView("templates");
      setViewing(null);
      setSelectedTemplateId(id);
      setTemplateLoading(true);
      try {
        const res = await fetchEmailTemplateById(id);
        const tpl = res.data?.template;
        setEditorTemplate(tpl);
        setDraft({ label: tpl.label, subject: tpl.subject, content_html: tpl.content_html });
        setHtmlMode(false);
        refreshPreview(tpl.subject, tpl.content_html);
      } catch (err) {
        toast.error(err.message || "Could not load template.");
      } finally {
        setTemplateLoading(false);
      }
    },
    [refreshPreview]
  );

  const saveTemplate = async () => {
    if (!editorTemplate) return;
    if (!draft.label.trim() || !draft.subject.trim() || !draft.content_html.trim()) {
      toast.error("Label, subject, and body are all required.");
      return;
    }
    setSavingTemplate(true);
    try {
      const res = await updateEmailTemplate(editorTemplate.id, {
        label: draft.label,
        subject: draft.subject,
        content_html: draft.content_html,
      });
      setEditorTemplate(res.data?.template);
      setTemplates((prev) =>
        prev.map((t) => (t.id === editorTemplate.id ? { ...t, label: draft.label, subject: draft.subject, content_html: draft.content_html } : t))
      );
      toast.success("Template saved.");
      refreshPreview(draft.subject, draft.content_html);
    } catch (err) {
      toast.error(err.message || "Could not save template.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const saveRouting = async (nextRouting) => {
    setSavingRouting(true);
    try {
      const res = await saveEmailTemplateRouting(nextRouting);
      setTemplateRouting(res.data?.template_routing || nextRouting);
      toast.success("Email routing saved.");
    } catch (err) {
      toast.error(err.message || "Could not save email routing.");
    } finally {
      setSavingRouting(false);
    }
  };

  const createTemplateFromModal = async ({ template_type, role_group, stage }) => {
    setCreatingTemplate(true);
    try {
      const roleOpt = roleGroupOptions(audiences).find((o) => o.key === role_group);
      const roleLabel = roleOpt?.label || role_group;
      const typeLabel =
        template_type === "invite"
          ? "Invitation"
          : template_type === "resend"
            ? "Resend"
            : `Reminder stage ${stage || 1}`;
      const label =
        role_group === "all"
          ? `All roles — ${typeLabel}`
          : `${roleLabel} — ${typeLabel}`;
      const res = await createEmailTemplate({
        template_type,
        role_group,
        label,
        subject: "",
        content_html: "",
        ...(template_type === "auto_reminder" ? { stage: stage || 1 } : {}),
      });
      const tpl = res.data?.template;
      setTemplates((prev) => [...prev, tpl]);
      closeNewTemplateModal();
      toast.success("Template created — edit the body now.");
      selectTemplate(tpl.id);
    } catch (err) {
      toast.error(err.message || "Could not create template.");
    } finally {
      setCreatingTemplate(false);
    }
  };

  const matchWorkspaceVoice = async () => {
    if (!editorTemplate || matchingVoice) return;
    setMatchingVoice(true);
    try {
      const roleLabel =
        audiences.find((a) => a.key === editorTemplate.role_group)?.label ||
        editorTemplate.role_group;
      const res = await fetchEmailTemplateDraft({
        template_type: editorTemplate.template_type,
        role_group: editorTemplate.role_group,
        role_label: roleLabel,
        stage: editorTemplate.stage || 1,
        subject: draft.subject,
        content_html: draft.content_html,
      });
      const next = res.data?.draft;
      if (!next?.subject && !next?.content_html) {
        toast.error("Could not match workspace voice.");
        return;
      }
      const subject = next.subject || draft.subject;
      const contentHtml = next.content_html || draft.content_html;
      if (subject === draft.subject && contentHtml === draft.content_html) {
        toast.success("This draft already matches the available voice.");
        return;
      }
      setDraft((d) => ({ ...d, subject, content_html: contentHtml }));
      refreshPreview(subject, contentHtml);
      toast.success("Draft updated to match workspace voice. Save to keep it.");
    } catch (err) {
      toast.error(err.message || "Could not match workspace voice.");
    } finally {
      setMatchingVoice(false);
    }
  };

  const generateAllRoleDrafts = async (opts = {}) => {
    setGeneratingAllDrafts(true);
    try {
      const perRole =
        opts.perRole ??
        Boolean(
          templateRouting.invite?.per_role ||
            templateRouting.resend?.per_role ||
            templateRouting.auto_reminder?.per_role
        );
      const res = await generateEmailTemplateDrafts({ per_role: perRole });
      const created = res.data?.templates || [];
      if (!created.length) {
        toast.success("All role templates already exist.");
      } else {
        await load();
        toast.success(`Created ${created.length} AI template draft(s). Open each to review.`);
      }
    } catch (err) {
      toast.error(err.message || "Could not generate template drafts.");
    } finally {
      setGeneratingAllDrafts(false);
    }
  };

  const insertToken = (token) => {
    setDraft((d) => ({ ...d, content_html: `${d.content_html}${token}` }));
  };

  const saveSteps = async () => {
    for (const s of steps) {
      const hours = Number(s.offset_hours);
      if (!hours || hours <= 0) {
        toast.error(`"${s.label || "Untitled step"}" needs a positive hour offset.`);
        return;
      }
      if (Boolean(s.dispatch_window_start) !== Boolean(s.dispatch_window_end)) {
        toast.error(`"${s.label || "Untitled step"}" has an incomplete send window.`);
        return;
      }
      if (s.dispatch_window_start && s.dispatch_window_start >= s.dispatch_window_end) {
        toast.error(`"${s.label || "Untitled step"}"'s send window start must be before its end.`);
        return;
      }
    }
    setSavingSteps(true);
    try {
      const audienceKeys = new Set(audiences.map((a) => a.key));
      const payload = steps.map(({ _key, template_id_leadership, template_id_pe_board, ...rest }) => ({
        ...rest,
        anchor: "INVITATION_SENT",
        offset_hours: Number(rest.offset_hours),
        // Drop undefined values so cleared selects are sent as "no template"
        // rather than as the string "undefined".
        template_ids: Object.fromEntries(
          Object.entries(rest.template_ids || {}).filter(
            ([key, v]) => v && (audienceKeys.size === 0 || audienceKeys.has(key))
          )
        ),
      }));
      const res = await saveReminderSteps({ steps: payload });
      const rawSteps = res.data?.rule?.steps || [];
      setSteps(rawSteps.map((s) => ({ ...s, _key: s.step_id || makeKey() })));
      toast.success("Schedule saved.");
    } catch (err) {
      toast.error(err.message || "Could not save schedule.");
    } finally {
      setSavingSteps(false);
    }
  };

  const addStep = () => {
    const key = makeKey();
    setSteps((prev) => [
      ...prev,
      {
        _key: key,
        step_id: null,
        label: `Reminder ${prev.length + 1}`,
        anchor: "INVITATION_SENT",
        offset_hours: 24,
        is_enabled: true,
        dispatch_window_start: null,
        dispatch_window_end: null,
        template_ids: {},
      },
    ]);
    return key;
  };

  // Duplicated steps drop step_id so the server mints a fresh one instead of
  // two steps colliding on the same identity.
  const duplicateStep = (key) => {
    setSteps((prev) => {
      const idx = prev.findIndex((s) => s._key === key);
      if (idx < 0) return prev;
      const source = prev[idx];
      const copy = {
        ...source,
        _key: makeKey(),
        step_id: null,
        label: `${source.label} (copy)`,
        template_ids: { ...(source.template_ids || {}) },
      };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };

  const saveAudiences = async (drafts) => {
    setSavingAudiences(true);
    try {
      for (const d of drafts) {
        await updateEmailAudience(d.id, {
          label: d.label,
          match_designations: d.match_designations,
        });
      }
      const cfg = await fetchEmailTemplateConfig();
      setAudiences(cfg.data?.audiences || []);
      toast.success("Audiences saved.");
      setAudienceModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Could not save audiences.");
    } finally {
      setSavingAudiences(false);
    }
  };

  const removeTemplate = async (tpl) => {
    setDeleteDialogTarget(tpl);
  };

  const confirmDeleteTemplate = async () => {
    if (!deleteDialogTarget) return;
    const tpl = deleteDialogTarget;
    setDeleteDialogTarget(null);
    setDeletingId(tpl.id);
    try {
      await deleteEmailTemplate(tpl.id);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      if (selectedTemplateId === tpl.id) {
        setSelectedTemplateId(null);
        setEditorTemplate(null);
      }
      if (viewing?.templateId === tpl.id) setViewing(null);
      toast.success("Template deleted.");
    } catch (err) {
      // Server refuses when a reminder step still points at it, or when it's a
      // built-in default — surface that reason rather than a generic failure.
      toast.error(err.message || "Could not delete template.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleTestSend = async (e) => {
    e.preventDefault();
    if (!editorTemplate) return;
    setTestSending(true);
    try {
      await sendTestEmail({
        template_id: editorTemplate.id,
        override_email: testSendEmail.trim(),
        subject: draft.subject,
        content_html: draft.content_html,
      });
      toast.success(`Test email sent to ${testSendEmail.trim()}.`);
      setTestSendOpen(false);
    } catch (err) {
      toast.error(err.message || "Could not send test email.");
    } finally {
      setTestSending(false);
    }
  };

  const addAudience = async (payload) => {
    try {
      const res = await createEmailAudience(payload);
      const created = res.data?.audience;
      setAudiences((prev) => [...prev, created]);
      toast.success(`Audience "${created.label}" added.`);
      return created;
    } catch (err) {
      toast.error(err.message || "Could not create audience.");
      return null;
    }
  };

  const loadHistory = useCallback(async (page = 1) => {
    setLogLoading(true);
    try {
      const res = await fetchDispatchLog({ page, page_size: 25 });
      setLogItems(res.data?.items || []);
      setLogTotal(res.data?.total || 0);
      setLogPage(res.data?.page || 1);
    } catch (err) {
      toast.error(err.message || "Could not load dispatch history.");
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === "history") loadHistory(1);
  }, [view, loadHistory]);

  const audienceRoleRank = useMemo(() => {
    const rank = new Map();
    audiences.forEach((a, index) => {
      rank.set(a.key, a.sort_order ?? index);
    });
    return rank;
  }, [audiences]);

  const templatesForAudiences = useMemo(() => {
    if (!audiences.length) return templates;
    const keys = new Set(audiences.map((a) => a.key));
    keys.add("all");
    return templates.filter((t) => !t.role_group || keys.has(t.role_group));
  }, [templates, audiences]);

  const audienceLabelForTemplate = useCallback(
    (template) => {
      if (template.role_group === "all") return "All roles";
      return audiences.find((a) => a.key === template.role_group)?.label || template.role_group;
    },
    [audiences]
  );

  const autoReminderTemplates = templatesForAudiences.filter((t) => t.template_type === "auto_reminder");

  const templateGroups = useMemo(() => {
    const order = ["invite", "resend", "auto_reminder"];
    const titles = { invite: "Invites", resend: "Resends", auto_reminder: "Reminders" };
    const rank = (type) => {
      const index = order.indexOf(type);
      return index === -1 ? order.length : index;
    };
    const roleRank = (roleGroup) =>
      audienceRoleRank.has(roleGroup) ? audienceRoleRank.get(roleGroup) : 999;
    const sorted = [...templatesForAudiences].sort((a, b) => {
      const byType = rank(a.template_type) - rank(b.template_type);
      if (byType) return byType;
      const byRole = roleRank(a.role_group) - roleRank(b.role_group);
      if (byRole) return byRole;
      const stageA = a.stage == null || a.stage === "" ? 0 : Number(a.stage);
      const stageB = b.stage == null || b.stage === "" ? 0 : Number(b.stage);
      if (stageA !== stageB) return stageA - stageB;
      return String(a.label || "").localeCompare(String(b.label || ""));
    });
    const groups = [];
    for (const template of sorted) {
      const key = order.includes(template.template_type) ? template.template_type : "other";
      let group = groups.find((item) => item.key === key);
      if (!group) {
        group = { key, title: titles[key] || "Other", items: [] };
        groups.push(group);
      }
      group.items.push(template);
    }
    return groups;
  }, [templatesForAudiences, audienceRoleRank]);

  return (
    <div className="min-h-full w-full bg-white">
      <Toaster position="top-center" />
      <div className="w-full px-6 py-6">
        <header className="mb-2 flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70 mb-2">Email</p>
            <h1 className="text-2xl font-bold text-gray-900">Mail Manager</h1>
            <p className="text-sm text-slate-500 mt-1">
              Configure automated reminder timing and edit the emails they send.
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-1 bg-slate-50 shrink-0" role="tablist" aria-label="Mail manager">
            {[
              { key: "editor", label: "Editor" },
              { key: "templates", label: "Templates" },
              { key: "history", label: "Dispatch history" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={view === tab.key}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                  view === tab.key ? "bg-white shadow-sm text-[#004f73]" : "text-slate-500"
                }`}
                onClick={() => {
                  setViewing(null);
                  setView(tab.key);
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </header>
        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#004f73]">
            <Spinner />
          </div>
        ) : view === "history" ? (
          <DispatchHistory items={logItems} total={logTotal} page={logPage} loading={logLoading} onPage={loadHistory} />
        ) : view === "templates" ? (
          /* Template library on the left, editor on the right. Collapses to a
             single column below lg. */
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(300px,420px)_1fr] gap-6 items-start">
            <div className="lg:max-h-[calc(100vh-13rem)] lg:overflow-y-auto lg:pr-3 lg:-mr-3 pb-2">
              <EmailTemplateRoutingPanel
                routing={templateRouting}
                templates={templatesForAudiences}
                audiences={audiences}
                designations={designations}
                allowPerRole={allowPerRoleEmailTemplates}
                isClassicWorkspace={isClassicEmailWorkspace}
                saving={savingRouting}
                onSaveRouting={saveRouting}
                onEditTemplate={(id) => selectTemplate(id)}
                onPreviewTemplate={(templateId, audienceLabel) =>
                  setViewing({ templateId, audienceLabel })
                }
                onGenerateAllDrafts={generateAllRoleDrafts}
                generatingAllDrafts={generatingAllDrafts}
                onCreateMissing={openNewTemplateModal}
              />
              <section>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <h3 className="text-base font-bold text-slate-900 leading-6">All templates</h3>
                  <button
                    type="button"
                    className="text-xs font-semibold text-[#004f73] hover:underline shrink-0"
                    onClick={() => openNewTemplateModal()}
                  >
                    New template
                  </button>
                </div>
                {isClassicEmailWorkspace ? (
                  <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                    Default 5X copy is per audience (CEO &amp; Leadership vs PE &amp; Board). Shared
                    &ldquo;All roles&rdquo; templates apply only when you turn off per-role mode above.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                    Default is one shared email for every role. Enable per-role mode above to use
                    separate templates per audience ({audiences.map((a) => a.label).join(", ")}).
                  </p>
                )}
                <div className="space-y-5">
                  {templateGroups.length ? (
                    templateGroups.map((group) => (
                      <section key={group.key}>
                        <h4 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
                          {group.title}
                        </h4>
                        <div className="space-y-2">
                          {group.items.map((t) => {
                            const audienceLabel = audienceLabelForTemplate(t);
                            const display = templateDisplay(t, audienceLabel);
                            return (
                              <div
                                key={t.id}
                                className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm border transition ${
                                  selectedTemplateId === t.id
                                    ? "border-[#004f73] bg-[#004f73]/5"
                                    : "border-slate-200 hover:bg-slate-50"
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => selectTemplate(t.id)}
                                  className={`flex-1 min-w-0 text-left ${
                                    selectedTemplateId === t.id ? "text-[#004f73]" : "text-slate-800"
                                  }`}
                                >
                                  <div className="font-semibold truncate leading-5">
                                    {display.audienceFull || display.title}
                                  </div>
                                  {display.audienceFull && display.title ? (
                                    <p className="text-xs text-slate-500 mt-0.5 truncate">{display.title}</p>
                                  ) : null}
                                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                    {display.stage ? (
                                      <MetaBadge className="bg-slate-100 text-slate-600">
                                        Stage {display.stage}
                                      </MetaBadge>
                                    ) : null}
                                    {display.timing ? (
                                      <MetaBadge className="bg-white text-slate-500 border border-slate-200">
                                        {display.timing}
                                      </MetaBadge>
                                    ) : null}
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  className="shrink-0 p-1.5 rounded-md text-[#004f73] hover:bg-[#004f73]/10"
                                  onClick={() => setViewing({ templateId: t.id, audienceLabel })}
                                  aria-label={`Preview ${display.audienceFull || display.title}`}
                                  title="Preview this email"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  type="button"
                                  className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                  onClick={() => removeTemplate(t)}
                                  disabled={deletingId === t.id}
                                  aria-label={`Delete ${display.audienceFull || display.title}`}
                                  title="Delete this template"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    ))
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center">
                      <p className="text-sm font-medium text-slate-600">No templates yet</p>
                      <p className="text-xs text-slate-400 mt-1">Create one to customize reminder emails.</p>
                    </div>
                  )}
                </div>
              </section>
            </div>

            <div className="lg:sticky lg:top-6">
              {viewing ? (
                <TemplatePreviewPanel
                  templateId={viewing.templateId}
                  audienceLabel={viewing.audienceLabel}
                  onClose={() => setViewing(null)}
                  onEdit={(id) => selectTemplate(id)}
                />
              ) : !selectedTemplateId ? (
                <div className="flex flex-col items-center justify-center h-full min-h-[420px] rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 text-center">
                  <Mail size={28} className="text-slate-300 mb-3" />
                  <p className="text-sm font-medium text-slate-600">No template open</p>
                  <p className="text-xs text-slate-400 mt-1 max-w-xs leading-5">
                    Select a template from the list on the left to customize what each reminder says.
                  </p>
                </div>
              ) : templateLoading || !editorTemplate ? (
                <div className="flex items-center justify-center min-h-[420px] text-[#004f73]">
                  <Spinner />
                </div>
              ) : (
                <TemplateEditor
                  editorTemplate={editorTemplate}
                  draft={draft}
                  setDraft={setDraft}
                  htmlMode={htmlMode}
                  setHtmlMode={setHtmlMode}
                  saving={savingTemplate}
                  onSave={saveTemplate}
                  onSendTest={() => setTestSendOpen(true)}
                  onInsertToken={insertToken}
                  previewHtml={previewHtml}
                  previewLoading={previewLoading}
                  previewWidth={previewWidth}
                  setPreviewWidth={setPreviewWidth}
                  onRefreshPreview={() => refreshPreview(draft.subject, draft.content_html)}
                  tokens={tokens}
                  audiences={audiences}
                  onMatchVoice={matchWorkspaceVoice}
                  matchingVoice={matchingVoice}
                />
              )}
            </div>
          </div>
        ) : (
          /* Reminder cadence. A step's email preview opens beside it when requested. */
          <div
            className={
              viewing
                ? "grid grid-cols-1 lg:grid-cols-[minmax(300px,420px)_1fr] gap-6 items-start"
                : "max-w-xl"
            }
          >
            <div className="lg:max-h-[calc(100vh-13rem)] lg:overflow-y-auto lg:pr-3 lg:-mr-3 space-y-8 pb-2">
              <SchedulerStatusBanner scheduler={scheduler} />
              <section>
                <div className="mb-3">
                  <h3 className="text-base font-bold text-slate-900 leading-6">Reminder schedule</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {steps.length
                      ? `${steps.length} reminder${steps.length === 1 ? "" : "s"} in your cadence`
                      : "Set when to follow up with participants"}
                  </p>
                </div>
              <ReminderStepList
                steps={steps}
                templates={autoReminderTemplates}
                audiences={audiences}
                anchors={anchors}
                onChange={setSteps}
                onAddStep={addStep}
                onDuplicateStep={duplicateStep}
                onViewTemplate={(templateId, audienceLabel) =>
                  setViewing({ templateId, audienceLabel })
                }
                onSave={saveSteps}
                saving={savingSteps}
                canSave={canManageSteps}
                saveTitle={
                  canManageSteps ? "" : "Requires the Configure automated reminder cadence permission"
                }
                allowAudienceTemplateOverrides={
                  isClassicEmailWorkspace || Boolean(templateRouting.auto_reminder?.per_role)
                }
              />
                {audiences.length ? (
                  <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
                    Audiences:{" "}
                    {audiences.map((a) => a.label).join(", ")}.{" "}
                    <button
                      type="button"
                      className="font-semibold text-[#004f73] hover:underline"
                      onClick={() => setAudienceModalOpen(true)}
                    >
                      Manage audiences
                    </button>
                  </p>
                ) : null}
              </section>
            </div>

            {viewing ? (
              <div className="lg:sticky lg:top-6">
                <TemplatePreviewPanel
                  templateId={viewing.templateId}
                  audienceLabel={viewing.audienceLabel}
                  onClose={() => setViewing(null)}
                  onEdit={(id) => selectTemplate(id)}
                />
              </div>
            ) : null}
          </div>
        )}
      </div>

      {audienceModalOpen ? (
        <AudienceManagerModal
          audiences={audiences}
          saving={savingAudiences}
          onSave={saveAudiences}
          onCreate={addAudience}
          onClose={() => setAudienceModalOpen(false)}
        />
      ) : null}

      {/* Delete template confirm dialog */}
      {deleteDialogTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="del-tpl-title"
            className="w-full max-w-md rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_24px_60px_-12px_rgba(0,79,115,0.4)]"
          >
            <div className="mb-4 flex items-start gap-3.5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-red-50 text-red-600">
                <AlertTriangle className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <h2 id="del-tpl-title" className="text-lg font-bold text-slate-900">
                  Delete template?
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  <span className="font-semibold text-slate-700">&ldquo;{deleteDialogTarget.label}&rdquo;</span> will be permanently removed. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2.5">
              <button
                type="button"
                className="rounded-full px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100"
                onClick={() => setDeleteDialogTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteTemplate}
                className="rounded-full bg-gradient-to-b from-red-500 to-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_3px_10px_-2px_rgba(190,18,60,0.5)] transition-all hover:from-red-500 hover:to-red-700 active:translate-y-px"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Test-send modal */}
      <NewEmailTemplateModal
        open={newTemplateOpen}
        onClose={closeNewTemplateModal}
        audiences={audiences}
        saving={creatingTemplate}
        initialValues={newTemplatePrefill}
        onCreate={createTemplateFromModal}
      />

      {testSendOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="test-send-title"
            className="w-full max-w-sm rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_24px_60px_-12px_rgba(0,79,115,0.4)]"
          >
            <h2 id="test-send-title" className="mb-1 text-lg font-bold text-slate-900">
              Send test email
            </h2>
            <p className="mb-5 text-sm text-slate-500">
              A copy of the current draft will be sent using sample participant data.
            </p>
            <form onSubmit={handleTestSend} className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5" htmlFor="test-send-email">
                  Recipient email
                </label>
                <input
                  id="test-send-email"
                  type="email"
                  required
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                  value={testSendEmail}
                  onChange={(e) => setTestSendEmail(e.target.value)}
                />
              </div>
              <div className="flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  className="rounded-full px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100"
                  onClick={() => setTestSendOpen(false)}
                  disabled={testSending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={testSending}
                  className="rounded-full bg-gradient-to-b from-[#0a7ea4] to-[#004f73] px-5 py-2.5 text-sm font-bold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_3px_10px_-2px_rgba(0,79,115,0.55)] transition-all hover:from-[#0d8cb5] hover:to-[#065a82] disabled:opacity-50"
                >
                  {testSending ? "Sending…" : "Send test"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TemplateEditor({
  editorTemplate,
  draft,
  setDraft,
  htmlMode,
  setHtmlMode,
  saving,
  onSave,
  onSendTest,
  onInsertToken,
  previewHtml,
  previewLoading,
  previewWidth,
  setPreviewWidth,
  onRefreshPreview,
  tokens,
  audiences,
  onMatchVoice,
  matchingVoice,
}) {
  const audienceLabel =
    audiences.find((a) => a.key === editorTemplate.role_group)?.label || editorTemplate.role_group;

  // Device preview is a third tab rather than an always-on pane under the
  // editor: at 65% width the editor and a full-bleed iframe competed for the
  // same vertical space and neither got enough of it.
  const tab = previewWidth === "off" ? (htmlMode ? "html" : "rich") : "preview";
  const setTab = (next) => {
    if (next === "rich") {
      setHtmlMode(false);
      setPreviewWidth("off");
    } else if (next === "html") {
      setHtmlMode(true);
      setPreviewWidth("off");
    } else {
      setPreviewWidth("desktop");
      onRefreshPreview();
    }
  };

  const TABS = [
    { key: "rich", label: "Rich Editor" },
    { key: "html", label: "HTML Code" },
    { key: "preview", label: "Device Preview" },
  ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="border-b border-slate-200 bg-white px-5 pt-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="min-w-0 flex-1">
            <input
              className="w-full text-base font-bold text-slate-900 leading-6 border-0 border-b border-transparent focus:border-slate-300 focus:outline-none bg-transparent px-0"
              value={draft.label}
              onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
              aria-label="Template name"
            />
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-[#004f73]/10 text-[#004f73]">
                {shortAudienceLabel(audienceLabel) || audienceLabel}
              </span>
              <span
                className={`text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full ${
                  editorTemplate.source === "admin"
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {editorTemplate.source === "admin" ? "Edited" : "Default"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              className="px-4 py-2 text-sm font-semibold text-[#004f73] border border-[#004f73]/30 hover:bg-[#004f73]/5 rounded-lg transition-colors"
              onClick={onSendTest}
            >
              Send test
            </button>
            <button
              type="button"
              className="px-4 py-2 text-sm font-semibold text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg disabled:opacity-50 transition-colors"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save template"}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1" role="tablist" aria-label="Editor mode">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`px-3.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-[#004f73] text-[#004f73]"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5 space-y-5">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
            Subject line
          </label>
          <input
            className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
            value={draft.subject}
            onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
          />
          <button
            type="button"
            className="mt-2 text-xs font-semibold text-[#004f73] hover:underline disabled:opacity-50"
            onClick={onMatchVoice}
            disabled={matchingVoice || saving}
          >
            {matchingVoice ? "Matching workspace voice…" : "Match workspace voice"}
          </button>
        </div>

        {tab === "preview" ? (
          <div>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5">
                Device preview
                <span
                  className="relative group inline-flex"
                  title="Sample data. Real sends use each recipient's details."
                >
                  <Info size={13} className="text-slate-400" />
                </span>
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                  {["mobile", "desktop"].map((w) => (
                    <button
                      key={w}
                      type="button"
                      className={`px-2.5 py-1 text-xs font-medium rounded capitalize ${
                        previewWidth === w ? "bg-white shadow-sm text-[#004f73]" : "text-slate-500"
                      }`}
                      onClick={() => setPreviewWidth(w)}
                    >
                      {w}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="text-xs font-semibold text-[#004f73] hover:underline disabled:opacity-50"
                  onClick={onRefreshPreview}
                  disabled={previewLoading}
                >
                  {previewLoading ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </div>
            <div
              className={`border border-slate-200 rounded-xl overflow-hidden bg-slate-50 transition-all ${
                previewWidth === "mobile" ? "max-w-sm mx-auto" : "w-full"
              }`}
            >
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={previewHtml}
                className="w-full bg-white"
                style={{ height: 560, border: 0 }}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="sticky top-0 z-10 -mx-5 px-5 py-2.5 bg-white/95 backdrop-blur border-y border-slate-100">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                Insert variable
              </p>
              <div className="flex flex-wrap gap-1.5">
                {tokens.map((t) => (
                  <button
                    key={t.token}
                    type="button"
                    className="group relative px-2.5 py-1 text-xs font-medium text-[#004f73] bg-[#004f73]/8 hover:bg-[#004f73]/15 rounded-full transition-colors"
                    onClick={() => onInsertToken(`{{${t.token}}}`)}
                  >
                    + {t.label}
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 hidden group-hover:block whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-mono text-white shadow-lg z-20"
                    >
                      {`{{${t.token}}}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {tab === "html" ? (
              <textarea
                className="w-full h-[420px] border border-slate-200 rounded-lg px-3 py-2.5 text-xs font-mono leading-6 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
                value={draft.content_html}
                onChange={(e) => setDraft((d) => ({ ...d, content_html: e.target.value }))}
                spellCheck={false}
              />
            ) : (
              <ReactQuill
                theme="snow"
                value={draft.content_html}
                onChange={(html) => setDraft((d) => ({ ...d, content_html: html }))}
                modules={quillModules}
                className="bg-white [&_.ql-editor]:min-h-[340px] [&_.ql-editor]:leading-7"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SchedulerStatusBanner({ scheduler }) {
  if (!scheduler || scheduler.auto_reminders_enabled) return null;
  return (
    <div className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-950">
      <AlertTriangle className="shrink-0 mt-0.5 text-amber-600" size={16} aria-hidden />
      <div>
        <p className="font-semibold text-amber-900">Automatic sending is off</p>
        <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
          Schedules are saved here, but reminder emails only go out when automatic reminders are
          enabled for this environment. Dispatch history will stay empty until then.
        </p>
      </div>
    </div>
  );
}

function DispatchHistory({ items, total, page, loading, onPage }) {
  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center py-24 text-[#004f73]">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Recipient</th>
                <th className="text-left px-4 py-3">Subject</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Step</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3 text-gray-900">{row.email}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{row.subject}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        row.email_type === "test" ? "text-amber-700 bg-amber-100" : "text-slate-600 bg-slate-100"
                      }`}
                    >
                      {row.email_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{row.reminder_step_label || "—"}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        row.status === "sent" ? "text-emerald-700 bg-emerald-100" : "text-red-700 bg-red-100"
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                    {row.created_date ? new Date(row.created_date).toLocaleString() : ""}
                  </td>
                </tr>
              ))}
              {!items.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    No dispatch history yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-slate-500">
          Page {page} of {totalPages} · {total} total
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => onPage(page - 1)}
          >
            Prev
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg disabled:opacity-40"
            disabled={page >= totalPages}
            onClick={() => onPage(page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
